import styled from 'styled-components';
import * as Sentry from '@sentry/react';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useId } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Modal, ModalTitle, MODAL_OUTER_PADDING, MODAL_INNER_GAP } from 'design-system/src/components/Modal';
import Button from 'design-system/src/components/Button/Button';
import ButtonRow from 'design-system/src/components/ButtonRow/ButtonRow';
import { toast } from 'react-hot-toast';
import { Toast, StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { ErrorMessage } from 'design-system/src/components/ErrorMessage/ErrorMessage';
import { Tooltip } from 'design-system/src/components/Tooltip/Tooltip';
import HelpCircleFilled from 'design-system/src/assets/icons/helpCircleFilled.svg?react';

import type { OrgMember, OrgMemberRole } from '../../../api';
import { FormLabel } from '../../FormLabel';
import { client } from '../../../api';
import { Input } from '../../Input';
import { Dropdown, DropdownItem } from '../../Dropdown.tsx';
import { Role } from '../../../types.ts';
import { getRoleOptions } from '../../signup/utils.tsx';
import { EMAIL_VALIDATION_REGEX, DUPLICATE_EMAIL_ERROR } from '../../../constants.ts';

// custom ModalBody because the footer buttons are inside the form and can't easily be split out into the ModalFooter component
const CustomModalBody = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  flex-grow: 1;
  padding: ${MODAL_INNER_GAP} ${MODAL_OUTER_PADDING} ${MODAL_OUTER_PADDING};
  gap: ${MODAL_INNER_GAP};
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DropdownContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

type FormData = {
  first_name: string;
  last_name: string;
  email_address: string;
  role: OrgMemberRole;
  job_title?: Role;
};

export type EditMemberModalProps = {
  onClose: () => void;
  selectedMember: OrgMember;
  members: OrgMember[];
};

/**
 * A modal for editing a member of an organization.
 */
const EditMemberModal = ({ onClose, selectedMember, members }: EditMemberModalProps) => {
  const isTrainingMode = import.meta.env.VITE_TRAINING_MODE === 'true';
  const duplicateEmailErrorMessageId = useId();
  const invalidEmailErrorMessageId = useId();
  const queryClient = useQueryClient();

  const submit = useMutation<{ success: boolean }, AxiosError<{ error: string }>, FormData>({
    mutationFn: async (data) => {
      if (isTrainingMode) {
        console.warn('Training Mode: Mocking user creation, no real request sent.');
        // Simulating a successful response without sending the request
        return Promise.resolve({ success: true });
      }

      return client.post(`org-members/edit/${selectedMember.id}`, data).then((res) => res.data);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['orgMembers'] });
      toast.custom(() => <Toast variant="success">User updated</Toast>);
      onClose();
    },
    onError: async (error) => {
      await queryClient.invalidateQueries({ queryKey: ['orgMembers'] });
      toast.custom(() => <StandardErrorToast />);
      console.error(error);
      Sentry.captureException(error);
    },
  });

  const {
    handleSubmit,
    control,
    formState: { isValid, errors },
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      first_name: selectedMember?.name?.split(' ')[0] || '',
      last_name: selectedMember?.name?.split(' ')[1] || '',
      email_address: selectedMember?.email_address || '',
      role: selectedMember?.is_admin ? 'stytch_admin' : 'stytch_member',
      job_title: selectedMember?.job_title,
    },
  });

  const [emailValue] = useWatch({ control, name: ['email_address'] });
  const emailAlreadyExists =
    // if we're editing a member, it's expected that the email is the same as the selected member's email
    emailValue !== selectedMember?.email_address &&
    !!emailValue &&
    !!members.find((member) => member.email_address === emailValue);

  return (
    <Modal onClose={onClose}>
      <ModalTitle>Edit user</ModalTitle>
      <CustomModalBody>
        <StyledForm onSubmit={handleSubmit((data) => submit.mutate(data))}>
          <Controller
            control={control}
            name="first_name"
            rules={{ required: true }}
            render={({ field }) => <Input label="First name" {...field} />}
          />

          <Controller
            control={control}
            name="last_name"
            rules={{ required: true }}
            render={({ field }) => <Input label="Last name" {...field} />}
          />

          <Controller
            control={control}
            name="role"
            rules={{ required: true }}
            render={({ field }) => (
              <DropdownContainer>
                <FormLabel id="role-label">
                  <span className="inline-flex items-center gap-1">
                    Permissions
                    <Tooltip
                      maxWidth="300px"
                      childNotInteractive
                      content="Admins are granted permissions to manage users, and other team settings like NPI and office affiliations."
                    >
                      <HelpCircleFilled
                        title="help"
                        className="inline-block size-4 cursor-help align-middle text-grayscale-400"
                      />
                    </Tooltip>
                  </span>
                </FormLabel>
                <Dropdown
                  labelId="role-label"
                  id="role"
                  value={field.value}
                  onChange={field.onChange}
                  // the disabled fieldset isn't enough to disable the dropdown; we have to manually disable it too
                  disabled={submit.isPending || selectedMember.is_current_user}
                >
                  <DropdownItem value="stytch_member">Non-admin</DropdownItem>
                  <DropdownItem value="stytch_admin">Admin</DropdownItem>
                </Dropdown>
              </DropdownContainer>
            )}
          />

          <Controller
            control={control}
            name="job_title"
            rules={{ required: true }}
            render={({ field }) => (
              <DropdownContainer>
                <FormLabel id="job_title-label">Role</FormLabel>
                <Dropdown labelId="job_title-label" id="job_title" value={field.value ?? ''} onChange={field.onChange}>
                  {getRoleOptions()}
                </Dropdown>
              </DropdownContainer>
            )}
          />

          {/* wrapper div to stop the flex gap from separating the input and error message */}
          <div>
            <Controller
              control={control}
              name="email_address"
              rules={{ required: true, pattern: EMAIL_VALIDATION_REGEX }}
              render={({ field }) => (
                <Input
                  label="Email"
                  aria-describedby={`${duplicateEmailErrorMessageId} ${invalidEmailErrorMessageId}`}
                  {...field}
                />
              )}
            />
            <ErrorMessage id={duplicateEmailErrorMessageId} visible={emailAlreadyExists}>
              {DUPLICATE_EMAIL_ERROR}
            </ErrorMessage>
            <ErrorMessage id={invalidEmailErrorMessageId} visible={errors.email_address?.type === 'pattern'}>
              Please enter a valid email address
            </ErrorMessage>
          </div>

          {/* wrapper div to stop the flex gap from separating the buttons and error message */}
          <div>
            <ButtonRow>
              {/* TODO: disable if the current values are the same as the default values */}
              <Button type="button" onClick={onClose} variant="tertiary" size="sm">
                Back
              </Button>
              <Button type="submit" disabled={!isValid || emailAlreadyExists} isLoading={submit.isPending} size="sm">
                Save
              </Button>
            </ButtonRow>

            <ErrorMessage visible={submit.isError}>{submit.error?.response?.data.error}</ErrorMessage>
          </div>
        </StyledForm>
      </CustomModalBody>
    </Modal>
  );
};

export default EditMemberModal;
